import { useCallback, useContext, useMemo } from "react";

import type { ListTrackDto } from "@/lib/lutely-api/gen/dtos/listTrackDto";
import { AudioPlayerContext } from "./audioPlayerContext";

function useAudioPlayerContext() {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error(
      "AudioPlayer hooks must be used within AudioPlayerProvider",
    );
  }
  return context;
}

export function useAudioPlayerState() {
  const {
    currentTrack,
    currentTrackId,
    currentTimeMs,
    durationMs,
    progress,
    isPlaying,
    isReady,
    hasNext,
    hasPrevious,
    volume,
    isMuted,
  } = useAudioPlayerContext();
  return {
    currentTrack,
    currentTrackId,
    currentTimeMs,
    durationMs,
    progress,
    isPlaying,
    isReady,
    hasNext,
    hasPrevious,
    volume,
    isMuted,
  } as const;
}

export function useAudioPlayerControls() {
  const { togglePlay, playNext, playPrevious, seekTo, playTrack, pause } =
    useAudioPlayerContext();
  return {
    togglePlay,
    playNext,
    playPrevious,
    seekTo,
    playTrack,
    pause,
  } as const;
}

export function useAudioPlayerQueue() {
  const {
    queue,
    setQueue,
    registerTracks,
    clearQueue,
    playMode,
    setPlayMode,
    hydrated,
    restoredFromStorage,
  } = useAudioPlayerContext();
  return {
    queue,
    setQueue,
    registerTracks,
    clearQueue,
    playMode,
    setPlayMode,
    hydrated,
    restoredFromStorage,
  } as const;
}

export function useAudioPlayerVolume() {
  const { volume, isMuted, setVolume, toggleMute } = useAudioPlayerContext();
  return { volume, isMuted, setVolume, toggleMute } as const;
}

export function usePlayTrack(track: ListTrackDto) {
  const { currentTrackId, isPlaying: globalIsPlaying } =
    useAudioPlayerContext();
  const { registerTracks, playTrack } = useAudioPlayerContext();
  const { togglePlay } = useAudioPlayerContext();

  const isPlaying = useMemo(
    () => currentTrackId === track.trackId && globalIsPlaying,
    [currentTrackId, track.trackId, globalIsPlaying],
  );

  const playTrackHandler = useCallback(() => {
    if (currentTrackId === track.trackId) {
      togglePlay();
      return;
    }

    registerTracks([
      {
        id: track.trackId,
        trackId: track.trackId,
        title: track.title || "Untitled",
        creatorId: track.creator.creatorId,
        creator: track.creator.stageName || "",
        artworkUrl: track.imageUrl || "",
        src: (track.fileUrl as string) || "",
        durationMs: undefined,
      },
    ]);

    playTrack(track.trackId, {
      autoPlay: true,
      startPositionMs: 0,
      appendToHistory: true,
    });
  }, [
    currentTrackId,
    track.trackId,
    track.title,
    track.creator.stageName,
    track.creator.creatorId,
    track.fileUrl,
    track.imageUrl,
    registerTracks,
    playTrack,
    togglePlay,
  ]);

  return { isPlaying, playTrack: playTrackHandler, currentTrackId } as const;
}
